import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Link2 } from "lucide-react";
import { copyToClipboard } from "@/lib/resourceLinks";
import { useToast } from "@/hooks/use-toast";

interface Props {
  url: string;
  label?: string;
  copiedLabel?: string;
  size?: "sm" | "default" | "icon";
  variant?: "default" | "outline" | "secondary" | "ghost";
  className?: string;
  iconOnly?: boolean;
  title?: string;
  onCopied?: () => void;
}

/** Small reusable "Copy link" control with inline confirmation. */
export function CopyLinkButton({
  url,
  label = "Copy link",
  copiedLabel = "Copied",
  size = "sm",
  variant = "outline",
  className,
  iconOnly = false,
  title,
  onCopied,
}: Props) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      onCopied?.();
    } else {
      toast({
        title: "Couldn't copy automatically",
        description: url,
        variant: "destructive",
      });
    }
  };

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      className={`gap-2 ${className || ""}`}
      onClick={handleCopy}
      title={title || url}
      aria-label={title || label}
    >
      {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
      {!iconOnly && (copied ? copiedLabel : label)}
    </Button>
  );
}

export default CopyLinkButton;
